import React, { useEffect, useState, memo } from "react";
import TextareaAutosize from "react-textarea-autosize";
import { useGlobalState } from "@/hooks/useGlobalState";
import { APP_NAME } from "@/configs/app";
import { upperFirst } from "@/utils/helpers";
import { getBrowserMicPermission } from "@/utils/get-browser-mic-permission";
import { setValue } from "@/utils/storage";
import { useAgentTimeout } from "@/providers/AgentTimeoutProvider";
import shoppingBasket from "/images/shoping-basket.svg";
import Union from "/images/Union.svg";

interface ChatFooterTextProps {
  onBasketClick?: () => void;
  agentState: string;
  cartItems: number;
  sendChat: (message: string) => void;
}

export const ChatFooterText = memo(function ChatFooterText({
  onBasketClick,
  agentState,
  cartItems,
  sendChat,
}: ChatFooterTextProps) {
  const { setChatMode, setMicPermission } = useGlobalState();
  const { handleUserAction } = useAgentTimeout();
  const [messageText, setMessageText] = useState("");

  const handleSendMessage = () => {
    if (messageText.length > 0) {
      sendChat(messageText);
      setMessageText("");
      setValue("last_interaction", Date.now().toString());
      handleUserAction();
    }
  };

  useEffect(() => {
    if (
      agentState !== "connecting" &&
      agentState !== "initializing" &&
      agentState !== "disconnected"
    ) {
      const audio = document.querySelectorAll("#nut-widget-container audio");

      if (
        audio &&
        audio.length > 0 &&
        (audio[0] as HTMLAudioElement).muted === false
      ) {
        (audio[0] as HTMLAudioElement).muted = true;
      }
    }
  }, [agentState]);

  const handleBlur = () => {
    const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (isMobile) {
      handleSendMessage();
    }
  };

  return (
    <div className="text-chat-footer w-full flex items-center justify-center gap-[10px] bg-white pt-[20px]">
      <div className="text-caht-input w-full [@media(max-width:440px)]:w-[calc(100%-50px)] h-[68px] flex items-center gap-[16px] rounded-[1998px] border-2 border-[#D8D8D8] pl-[20px] pr-[20px]">
        <TextareaAutosize
          maxRows={3}
          style={{
            height: 46,
            boxShadow: "none",
          }}
          value={messageText}
          onChange={(event) => setMessageText(event.target.value)}
          onBlur={handleBlur}
          onKeyDown={(e) => {
            if (messageText.length > 0 && e.key === "Enter") {
              handleSendMessage();
            }
          }}
          disabled={
            agentState === "connecting" ||
            // agentState === "initializing" ||
            agentState === "disconnected"
          }
          placeholder={
            agentState === "connecting" || agentState === "initializing"
              ? `${upperFirst(agentState)}...`
              : `Reply to ${APP_NAME}...`
          }
          onHeightChange={() => setMessageText(messageText.replace("\n", ""))}
          className="flex-1 min-w-0 h-full bg-transparent focus:outline-none text-[16px] leading-[20.63px] tracking-[0%] text-black opacity-60"
        />

        <div className="flex items-center gap-[16px]">
          <button
            onClick={async () => {
              setChatMode("voice");

              setMicPermission(await getBrowserMicPermission());
            }}
          >
            <img
              src={Union}
              title="Switch to voice chat"
              className="w-[24.7px] h-[28.5px]"
            />
          </button>
        </div>
      </div>
      <button
        onClick={onBasketClick}
        className="flex-shrink-0 w-[68px] h-[68px] flex items-center justify-center rounded-full border-2 border-[#D8D8D8] relative"
      >
        {cartItems > 0 && (
          <div className="absolute -top-1 -right-1 gradient-wrap text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">
            {cartItems}
          </div>
        )}
        <img
          src={shoppingBasket}
          title="Open basket"
          alt="comment-icon"
          className="w-[23.3px] h-[22.17px]"
        />
      </button>
    </div>
  );
});
